<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div v-if="visible" class="fixed inset-0 z-50">
    <div class="absolute inset-0 bg-black/50" @click="close"></div>
    <div class="relative min-h-screen flex items-center justify-center p-4">
      <div class="relative bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
        <div class="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h3 class="text-lg font-semibold text-slate-900">产品详情</h3>
            <p class="text-xs text-slate-500">展示价：<span class="text-primary-600">{{ priceDisplay }}</span></p>
          </div>
          <button @click="close" class="rounded-full p-1 text-slate-400 hover:text-slate-600">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div v-if="loading" class="py-12 text-center text-sm text-slate-500">加载详情中...</div>

          <div v-else-if="detail">
            <div class="p-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
              <div class="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div class="text-xs text-slate-500">产品名称</div>
                <div class="mt-1 text-base font-semibold text-slate-900">{{ detail.name }}</div>
                <div class="mt-2 text-xs text-slate-500">产品编码：{{ detail.code }}</div>
              </div>
              <div class="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div class="text-xs text-slate-500">当前版本</div>
                <div class="mt-1 text-base font-semibold text-slate-900">v{{ detail.versionNo }}</div>
                <div class="mt-2 text-xs text-slate-500">状态：{{ detail.status === 1 ? '上架' : '下架' }}</div>
              </div>
              <div class="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div class="text-xs text-slate-500">类型</div>
                <div class="mt-1 text-base font-semibold text-slate-900">{{ categoryName }}</div>
                <div class="mt-2 text-xs text-slate-500">周期：{{ detail.durationDays }} 天</div>
              </div>
            </div>

            <div class="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div class="rounded-xl border border-slate-200 p-4">
                <h4 class="text-sm font-semibold text-slate-900">基础信息</h4>
                <dl class="mt-3 space-y-2 text-sm text-slate-600">
                  <div class="flex justify-between">
                    <span>摘要</span>
                    <span class="text-slate-900">{{ detail.summary || '-' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>服务范围</span>
                    <span class="text-slate-900">{{ detail.serviceScope || '-' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>交付物</span>
                    <span class="text-slate-900">{{ detail.deliverables || '-' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>注意事项</span>
                    <span class="text-slate-900">{{ detail.notices || '-' }}</span>
                  </div>
                  <div class="flex justify-between">
                    <span>关联方案审批</span>
                    <span class="text-slate-900">{{ detail.needApproval === 1 ? '需要审批' : '无需审批' }}</span>
                  </div>
                </dl>
              </div>

              <div class="rounded-xl border border-slate-200 p-4">
                <div class="flex items-center gap-2">
                  <svg class="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  <h4 class="text-sm font-semibold text-slate-900">流程步骤</h4>
                </div>
                <div class="mt-4 space-y-3">
                  <template v-if="processStepsList && processStepsList.length">
                    <div
                      v-for="(step, index) in processStepsList"
                      :key="index"
                      class="relative flex items-start gap-4 rounded-lg bg-gradient-to-br from-slate-50 to-white p-4 shadow-sm transition-all hover:shadow-md"
                    >
                      <div class="relative">
                        <div class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-sm font-semibold text-white shadow-sm">
                          {{ step.sort || index + 1 }}
                        </div>
                        <div
                          v-if="index < processStepsList.length - 1"
                          class="absolute left-1/2 top-10 h-3 w-px -translate-x-1/2 bg-slate-200"
                        ></div>
                      </div>
                      <div class="flex-1 pt-0.5">
                        <div class="text-sm font-semibold text-slate-900">{{ step.name }}</div>
                        <div v-if="step.notes" class="mt-2 rounded-md bg-amber-50 px-3 py-2 text-xs text-slate-700">
                          <div class="flex items-start gap-2">
                            <svg class="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{{ step.notes }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </template>
                  <div
                    v-else
                    class="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-slate-50/50 p-8"
                  >
                    <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                      <svg class="h-6 w-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                    <p class="mt-3 text-sm font-medium text-slate-600">暂无流程步骤</p>
                  </div>
                </div>
              </div>
            </div>

            <div class="mt-6 rounded-xl border border-slate-200 p-4">
              <h4 class="text-sm font-semibold text-slate-900">详情介绍</h4>
              <div
                class="mt-3 text-sm text-slate-600 leading-relaxed"
                v-html="detail.detailContent || '暂无内容'"
              ></div>
            </div>

            <div class="mt-6 rounded-xl border border-slate-200 p-4">
              <h4 class="text-sm font-semibold text-slate-900">所需资料</h4>
              <div class="mt-3 space-y-2">
                <div
                  v-for="material in detail.materials"
                  :key="material.materialDefinitionId"
                  class="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm"
                >
                  <div>
                    <span class="font-medium text-slate-900">{{ material.name }}</span>
                    <span class="ml-2 text-xs text-slate-500">{{ material.code }}</span>
                  </div>
                  <div class="flex items-center gap-3 text-xs">
                    <span :class="material.isRequired ? 'text-red-500' : 'text-slate-500'">{{ material.isRequired ? '必填' : '选填' }}</span>
                    <span class="text-slate-500">{{ material.allowMultiple ? '可多份' : '单份' }}</span>
                  </div>
                </div>
                <div v-if="!detail.materials?.length" class="rounded-lg border border-dashed border-slate-200 p-3 text-center text-xs text-slate-500">
                  暂无资料要求
                </div>
              </div>
            </div>

            <div class="mt-6 rounded-xl border border-slate-200 p-4">
              <h4 class="text-sm font-semibold text-slate-900">价格模型</h4>
              <div class="mt-4 space-y-4">
                <div v-for="model in detail.priceModels" :key="model.id" class="rounded-xl border border-slate-200 p-4">
                  <div class="flex flex-wrap items-center justify-between gap-2">
                    <div class="text-sm font-semibold text-slate-900">{{ pricingTypeLabel(model.pricingType) }}</div>
                    <div class="text-sm text-primary-600">{{ model.priceDisplay || '-' }}</div>
                  </div>

                  <div v-if="model.pricingType === 'RANGE'" class="mt-3 text-sm text-slate-600">
                    <div v-for="item in model.items" :key="item.id" class="rounded-lg bg-slate-50 px-3 py-2">
                      区间价：{{ item.priceMin }} - {{ item.priceMax }} {{ model.currency }}
                    </div>
                  </div>

                  <div v-else-if="model.pricingType === 'QUOTE'" class="mt-3 text-sm text-slate-600">
                    <div class="rounded-lg bg-slate-50 px-3 py-2">价格面议</div>
                  </div>

                  <div v-else class="mt-3 space-y-2 text-sm text-slate-600">
                    <div v-for="item in model.items" :key="item.id" class="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                      <span>{{ item.name || '价格项' }}</span>
                      <span class="text-slate-900">{{ item.price }} {{ model.currency }}</span>
                    </div>
                    <div v-if="!model.items?.length" class="rounded-lg bg-slate-50 px-3 py-2">暂无价格项</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="flex justify-end border-t border-slate-200 px-6 py-4">
          <button @click="close" class="rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-700 hover:bg-slate-200">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { getProductDetail } from '@/api'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  productId: {
    type: [Number, String],
    default: null
  },
  categories: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:modelValue', 'close'])

const visible = ref(false)
const loading = ref(false)
const detail = ref(null)

onMounted(() => {
  console.log('ProductDetailModal mounted, modelValue:', props.modelValue)
})

watch(() => props.modelValue, (val) => {
  visible.value = val
  if (val && props.productId) {
    loadDetail()
  }
}, { immediate: true })

watch(visible, (val) => {
  if (!val) {
    emit('update:modelValue', false)
  }
})

const categoryName = computed(() => {
  if (!detail.value?.categoryId) return '-'
  const category = props.categories.find(item => item.id === detail.value.categoryId)
  return category ? category.name : '-'
})

const priceDisplay = computed(() => {
  return detail.value?.priceModels?.[0]?.priceDisplay || detail.value?.summary || '-'
})

const processStepsList = computed(() => {
  const steps = detail.value?.processSteps

  // 如果是数组，直接排序返回
  if (Array.isArray(steps)) {
    return steps.sort((a, b) => (a.sort || 0) - (b.sort || 0))
  }

  // 如果是 JSON 字符串，解析后返回
  if (typeof steps === 'string' && steps.trim()) {
    try {
      const parsed = JSON.parse(steps)
      if (Array.isArray(parsed)) {
        return parsed.sort((a, b) => (a.sort || 0) - (b.sort || 0))
      }
    } catch (error) {
      console.error('解析流程步骤 JSON 失败:', error)
    }
  }

  return []
})

const pricingTypeLabel = (type) => {
  const map = {
    FIXED: '固定价',
    RANGE: '区间价',
    TIER: '阶梯价',
    ITEMIZED: '计项价',
    QUOTE: '面议',
    SUBSCRIPTION: '订阅价'
  }
  return map[type] || type
}

const loadDetail = async () => {
  if (!props.productId) return
  loading.value = true
  try {
    detail.value = await getProductDetail(props.productId)
  } catch (error) {
    console.error('加载产品详情失败:', error)
  } finally {
    loading.value = false
  }
}

const close = () => {
  visible.value = false
  emit('update:modelValue', false)
  emit('close')
}
</script>
